import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import Sidebar from '../../components/Sidebar';
import './movimentacao.css';
import { useInternals } from '../../context/internalContext';
import { toast } from 'react-toastify';
import Header from '../../components/header';
import Titulo from '../../components/Titulo/titulo';
import Filtro from '../../assets/icons/filtro.svg';
import FormattedInput from '../../components/FormatdInput/formatFunctions';
import ReactPaginate from 'react-paginate';
import Modal from '../../components/modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/free-solid-svg-icons';
import { useUsers } from '../../context/usersContext';
import { useAuxiliaries } from '../../context/auxiliariesContext';

function Moviment() {
  const { getMoviments, createMoviment } = useInternals();
  const { buildUnitReadParams } = useUsers();
  const { listTypeVisits, listTypeVisitor } = useAuxiliaries();

  const [movements, setMovements] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [activeSearch, setActiveSearch] = useState('');

  const [searchQuery, setSearchQuery] = useState('');
  const [internalNameFilter, setInternalNameFilter] = useState('');
  const [activeInternalName, setActiveInternalName] = useState('');
  const [purposeVisitFilter, setPurposeVisitFilter] = useState('');
  const [activePurposeVisit, setActivePurposeVisit] = useState('');
  const [movimentFilter, setMovimentFilter] = useState('');
  const [activeMoviment, setActiveMoviment] = useState('');
  const [typeVisitOptions, setTypeVisitOptions] = useState([]);
  const [typeVisitorOptions, setTypeVisitorOptions] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedMovement, setSelectedMovement] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isSubmittingExit, setIsSubmittingExit] = useState(false);
  // Debounce dos filtros de nome (visitante/interno): guarda a busca que ainda espera o usuário parar de digitar.
  const nameFilterTimerRef = useRef(null);

  const fetchMovements = useCallback(async ({
    name_visitor = '', name_internal = '', purpose_visit = '', moviment = '', page = 0, limit = 10,
  } = {}) => {
    // Busca imediata (outro filtro, página) cancela a de nome pendente, que viria com filtros antigos.
    clearTimeout(nameFilterTimerRef.current);
    try {
      const extra = {};
      if (name_visitor) extra.name_visitor = name_visitor;
      if (name_internal) extra.name_internal = name_internal;
      if (purpose_visit) extra.purpose_visit = purpose_visit;
      if (moviment) extra.moviment = moviment;
      const response = await getMoviments(buildUnitReadParams({ limit, offset: page * limit, ...extra }));

      if (response && Array.isArray(response.results)) {
        setMovements(response.results);
        setTotalCount(response.count || 0);
        setCurrentPage(page);
      } else {
        setMovements([]);
        setTotalCount(0);
        setCurrentPage(0);
      }
    } catch (error) {
      toast.error('Erro ao buscar movimentações.');
    }
  }, [getMoviments, buildUnitReadParams]);

  useEffect(() => {
    fetchMovements();
  }, [fetchMovements]);

  useEffect(() => (
    () => clearTimeout(nameFilterTimerRef.current)
  ), []);

  useEffect(() => {
    Promise.all([listTypeVisits(), listTypeVisitor()])
      .then(([visits, visitors]) => {
        const visitOpts = Array.isArray(visits?.results) ? visits.results : (Array.isArray(visits) ? visits : []);
        const visitorOpts = Array.isArray(visitors) ? visitors : [];
        setTypeVisitOptions(visitOpts);
        setTypeVisitorOptions(visitorOpts);
      })
      .catch(() => {
        setTypeVisitOptions([]);
        setTypeVisitorOptions([]);
      });
  }, [listTypeVisits, listTypeVisitor]);

  // Filtros de nome com debounce: só consulta a API quando o usuário para de digitar. Os dois filtros
  // dividem o timer, então cada busca registra como ativos os dois nomes que de fato enviou (paginação).
  const handleSearchQueryChange = (value) => {
    setSearchQuery(value);
    clearTimeout(nameFilterTimerRef.current);
    if (value.length === 0 || value.length >= 3) {
      nameFilterTimerRef.current = setTimeout(() => {
        setActiveSearch(value);
        setActiveInternalName(internalNameFilter);
        fetchMovements({
          name_visitor: value,
          name_internal: internalNameFilter,
          purpose_visit: purposeVisitFilter,
          moviment: movimentFilter,
          page: 0,
          limit: itemsPerPage,
        });
      }, 400);
    }
  };

  const handleInternalNameFilterChange = (value) => {
    setInternalNameFilter(value);
    clearTimeout(nameFilterTimerRef.current);
    if (value.length === 0 || value.length >= 3) {
      nameFilterTimerRef.current = setTimeout(() => {
        setActiveSearch(searchQuery);
        setActiveInternalName(value);
        fetchMovements({
          name_visitor: searchQuery,
          name_internal: value,
          purpose_visit: purposeVisitFilter,
          moviment: movimentFilter,
          page: 0,
          limit: itemsPerPage,
        });
      }, 400);
    }
  };

  const handlePurposeVisitFilterChange = (value) => {
    setPurposeVisitFilter(value);
    setActivePurposeVisit(value);
    fetchMovements({
      name_visitor: searchQuery,
      name_internal: internalNameFilter,
      purpose_visit: value,
      moviment: movimentFilter,
      page: 0,
      limit: itemsPerPage,
    });
  };

  const handleMovimentFilterChange = (value) => {
    setMovimentFilter(value);
    setActiveMoviment(value);
    fetchMovements({
      name_visitor: searchQuery,
      name_internal: internalNameFilter,
      purpose_visit: purposeVisitFilter,
      moviment: value,
      page: 0,
      limit: itemsPerPage,
    });
  };

  const resetFilters = () => {
    setSearchQuery('');
    setInternalNameFilter('');
    setPurposeVisitFilter('');
    setMovimentFilter('');
    setStartDate('');
    setEndDate('');
    setActiveSearch('');
    setActiveInternalName('');
    setActivePurposeVisit('');
    setActiveMoviment('');
    fetchMovements({ page: 0, limit: itemsPerPage });
  };

  const normalizeText = (value) =>
    String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();

  const getMovementStatusClass = (value) => {
    const normalized = normalizeText(value);
    if (normalized.includes('saida')) return 'movement-badge movement-badge-out';
    if (normalized.includes('entrada')) return 'movement-badge movement-badge-in';
    return 'movement-badge';
  };

  const displayedMovements = useMemo(() => {
    let filtered = [...movements];
    if (startDate) filtered = filtered.filter((m) => new Date(m.date) >= new Date(startDate));
    if (endDate) filtered = filtered.filter((m) => new Date(m.date) <= new Date(endDate));
    return filtered;
  }, [movements, startDate, endDate]);

  const pageCount = Math.ceil(totalCount / itemsPerPage);
  const formatDateTimeBR = (value) => {
    if (!value) return 'N/A';
    try {
      return new Date(value).toLocaleString('pt-BR');
    } catch {
      return value;
    }
  };

  const handleDarSaida = async () => {
    if (isSubmittingExit || !selectedMovement) return;
    setIsSubmittingExit(true);
    try {
      const payload = {
        visitor: selectedMovement.visitor,
        purpose_visit: selectedMovement.purpose_visit,
        internal: selectedMovement.internal,
        unit: selectedMovement.unit,
        moviment: 'Saída',
      };
      if (selectedMovement.type_visit) {
        payload.type_visit = selectedMovement.type_visit;
      }
      await createMoviment(payload);
      setIsDetailsModalOpen(false);
      setSelectedMovement(null);
      fetchMovements({
        name_visitor: activeSearch,
        name_internal: activeInternalName,
        purpose_visit: activePurposeVisit,
        moviment: activeMoviment,
        page: currentPage,
        limit: itemsPerPage,
      });
    } catch (error) {
      toast.error('Erro ao registrar saída.');
    } finally {
      setIsSubmittingExit(false);
    }
  };

  const openMovementDetails = (movement) => {
    setSelectedMovement(movement);
    setIsDetailsModalOpen(true);
  };

  return (
    <div>
      <Sidebar />
      <Header />
      <div className='container'>
        <Titulo>
          <h2>Movimentações na unidade</h2>
        </Titulo>

        <div className='movement-filters-bar'>
          <div className='movement-filter-item movement-filter-item-name'>
            <label>Visitante</label>
            <FormattedInput
              type='nome'
              placeholder='Digite nome do visitante'
              value={searchQuery}
              onChange={(e) => handleSearchQueryChange(e.target.value)}
              className='filter-input'
            />
          </div>

          <div className='movement-filter-item movement-filter-item-name'>
            <label>Interno</label>
            <FormattedInput
              type='nome'
              placeholder='Digite nome do interno'
              value={internalNameFilter}
              onChange={(e) => handleInternalNameFilterChange(e.target.value)}
              className='filter-input'
            />
          </div>

          <div className='movement-filter-item'>
            <label>Tipo de visita/visitante</label>
            <select
              value={purposeVisitFilter}
              onChange={(e) => handlePurposeVisitFilterChange(e.target.value)}
              className='filter-input'
            >
              <option value=''>Todos</option>
              {typeVisitOptions.length > 0 && (
                <optgroup label='Tipo de visita'>
                  {typeVisitOptions
                    .filter((option) => {
                      const name = normalizeText(option.type);
                      return name !== 'familiar' && !name.includes('virtual');
                    })
                    .map((option) => (
                      <option key={`visit-${option.id}`} value={option.type}>{option.type}</option>
                    ))}
                </optgroup>
              )}
              {typeVisitorOptions.length > 0 && (
                <optgroup label='Tipo de visitante'>
                  {typeVisitorOptions
                    .filter((option) => {
                      const name = normalizeText(option.name);
                      if (name === 'familiar' || name === 'intima' || name === 'intimo' || name === 'infantil') return false;
                      return !typeVisitOptions.some((tv) => normalizeText(tv.type) === name);
                    })
                    .map((option) => (
                      <option key={`visitor-${option.id}`} value={option.name}>{option.name}</option>
                    ))}
                </optgroup>
              )}
            </select>
          </div>

          <div className='movement-filter-item'>
            <label>Entrada/Saída</label>
            <select
              value={movimentFilter}
              onChange={(e) => handleMovimentFilterChange(e.target.value)}
              className='filter-input'
            >
              <option value=''>Todos</option>
              <option value='Entrada'>Entrada</option>
              <option value='Saída'>Saída</option>
            </select>
          </div>

          <div className='movement-filter-item'>
            <label>Data início</label>
            <input
              type='date'
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className='filter-date'
            />
          </div>

          <div className='movement-filter-item'>
            <label>Data término</label>
            <input
              type='date'
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className='filter-date'
            />
          </div>

          <div className='movement-filter-actions'>
            <button onClick={resetFilters} className='btn-reset btnAdcionar' type="button">
              Limpar<img src={Filtro} alt='icone' className='icon' />
            </button>
          </div>
        </div>

        <div className='content'>
          <div className='content-table'>
            {totalCount > 0 && (
              <p className='movement-results-count'>
                {totalCount} {totalCount === 1 ? 'movimentação encontrada' : 'movimentações encontradas'}
              </p>
            )}
            {displayedMovements.length > 0 ? (
              <table className='movements-table'>
                <thead>
                  <tr>
                    <th>Data/Hora</th>
                    <th>Nome</th>
                    <th>Tipo de visita</th>
                    <th>Movimentação</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {displayedMovements.map((movement, index) => (
                    <tr key={index}>
                      <td>{formatDateTimeBR(movement.date)}</td>
                      <td className="td-limited">{movement.visitor_name}</td>
                      <td>{movement.type_visit_name || movement.purpose_visit || '-'}</td>
                      <td>
                        <span className={getMovementStatusClass(movement.moviment)}>
                          {movement.moviment}
                        </span>
                      </td>
                      <td className="td-table-icon">
                        <button
                          type="button"
                          className="movement-view-button"
                          onClick={() => openMovementDetails(movement)}
                          title="Ver detalhes"
                          aria-label="Ver detalhes da movimentação"
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className='no-movements'>Nenhuma movimentação encontrada.</p>
            )}
            {totalCount > 0 && (
              <div className="pagination-options">
                <div>
                  <label htmlFor="movements-per-page">Itens por página:</label>
                  <select
                    id="movements-per-page"
                    value={itemsPerPage}
                    onChange={(e) => {
                      const newLimit = Number(e.target.value);
                      setItemsPerPage(newLimit);
                      fetchMovements({
                        name_visitor: activeSearch,
                        name_internal: activeInternalName,
                        purpose_visit: activePurposeVisit,
                        moviment: activeMoviment,
                        page: 0,
                        limit: newLimit,
                      });
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>

                {pageCount > 1 && (
                  <ReactPaginate
                    previousLabel={'<'}
                    nextLabel={'>'}
                    breakLabel={'...'}
                    pageCount={pageCount}
                    pageRangeDisplayed={3}
                    marginPagesDisplayed={1}
                    onPageChange={({ selected }) => fetchMovements({
                      name_visitor: activeSearch,
                      name_internal: activeInternalName,
                      purpose_visit: activePurposeVisit,
                      moviment: activeMoviment,
                      page: selected,
                      limit: itemsPerPage,
                    })}
                    containerClassName={'pagination'}
                    activeClassName={'activeNumber'}
                    previousClassName={'prev'}
                    nextClassName={'next'}
                    pageClassName={'page'}
                    breakClassName={'break-me'}
                    forcePage={currentPage}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal
        isOpen={isDetailsModalOpen}
        closeModal={() => {
          setIsDetailsModalOpen(false);
          setSelectedMovement(null);
        }}
        title="Detalhes da movimentação"
      >
        {selectedMovement && (
          <div className="movement-details-modal">
            <div className="movement-details-header">
              <div>
                <p className="movement-details-label">Visitante</p>
                <h4>{selectedMovement.visitor_name || 'N/A'}</h4>
              </div>
              <span className={getMovementStatusClass(selectedMovement.moviment)}>
                {selectedMovement.moviment || 'N/A'}
              </span>
            </div>

            <div className="movement-details-grid">
              <div className="movement-details-card">
                <span>Data/Hora</span>
                <strong>{formatDateTimeBR(selectedMovement.date)}</strong>
              </div>
              <div className="movement-details-card">
                <span>Interno(s)</span>
                <strong className="movement-break">
                  {selectedMovement.internal_name || 'N/A'}
                </strong>
              </div>
            </div>

            {selectedMovement.purpose_visit && (
              <div className="movement-details-block">
                <span className="movement-badge">{selectedMovement.purpose_visit}</span>
              </div>
            )}

            <div className="movement-details-block">
              <p className="movement-details-label">Observações</p>
              <div className="movement-details-text">
                {selectedMovement.observations || 'Sem observações'}
              </div>
            </div>

            {selectedMovement.moviment === 'Entrada' && (
              <div className="movement-details-actions">
                <button
                  type="button"
                  className="btn-dar-saida"
                  onClick={handleDarSaida}
                  disabled={isSubmittingExit}
                >
                  {isSubmittingExit ? 'Registrando...' : 'Dar saída'}
                </button>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

export default Moviment;
